import { AthleteProfile } from '../types';

export interface StatTrendResult {
  isUp: boolean;
  deltaText: string;
  recentAvg: number;
  overallAvg: number;
}

export interface AthletePerformanceTrends {
  pts: StatTrendResult;
  reb: StatTrendResult;
  ast: StatTrendResult;
  winRate: StatTrendResult;
}

/**
 * Calculates green up or red down trend indicators based on performance
 * in the athlete's last 3 logged games compared to their overall average.
 */
export function getAthletePerformanceTrends(athlete: AthleteProfile): AthletePerformanceTrends {
  const gamesPlayed = athlete.stats.basketball.gamesPlayed || 1;
  const overallPpg = athlete.stats.basketball.pts / gamesPlayed;
  const overallRpg = athlete.stats.basketball.reb / gamesPlayed;
  const overallApg = athlete.stats.basketball.ast / gamesPlayed;
  const overallWinRate = (athlete.winCount / ((athlete.winCount + athlete.lossCount) || 1)) * 100;

  // Use recorded recentGames if present
  let recent3 = athlete.recentGames || [];

  // Fallback heuristic if recentGames array is empty or < 3
  if (recent3.length === 0) {
    // Generate deterministic recent 3 game estimates based on athlete stats and ID
    const seed = (athlete.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % 10) - 4;
    recent3 = [
      { id: 'r1', date: '2026-08-04', pts: Math.max(2, Math.round(overallPpg + seed + 2)), reb: Math.max(1, Math.round(overallRpg + seed * 0.5 + 1)), ast: Math.max(1, Math.round(overallApg + seed * 0.5 + 1)), isWin: seed >= 0 },
      { id: 'r2', date: '2026-08-02', pts: Math.max(2, Math.round(overallPpg + seed + 1)), reb: Math.max(1, Math.round(overallRpg + seed * 0.5)), ast: Math.max(1, Math.round(overallApg + seed * 0.5)), isWin: seed >= -2 },
      { id: 'r3', date: '2026-07-30', pts: Math.max(2, Math.round(overallPpg + seed - 1)), reb: Math.max(1, Math.round(overallRpg - 0.5)), ast: Math.max(1, Math.round(overallApg - 0.5)), isWin: seed >= 1 },
    ];
  }

  const last3 = recent3.slice(0, 3);
  const count = last3.length;

  // Points calculation
  const sumPts = last3.reduce((acc, g) => acc + g.pts, 0);
  const recentPpg = sumPts / count;
  const ptsDiff = recentPpg - overallPpg;
  const ptsIsUp = ptsDiff >= 0;
  const ptsDeltaText = `${ptsIsUp ? '+' : ''}${ptsDiff.toFixed(1)} PPG (3G)`;

  // Rebounds calculation
  const sumReb = last3.reduce((acc, g) => acc + g.reb, 0);
  const recentRpg = sumReb / count;
  const rebDiff = recentRpg - overallRpg;
  const rebIsUp = rebDiff >= 0;
  const rebDeltaText = `${rebIsUp ? '+' : ''}${rebDiff.toFixed(1)} RPG (3G)`;

  // Assists calculation
  const sumAst = last3.reduce((acc, g) => acc + g.ast, 0);
  const recentApg = sumAst / count;
  const astDiff = recentApg - overallApg;
  const astIsUp = astDiff >= 0;
  const astDeltaText = `${astIsUp ? '+' : ''}${astDiff.toFixed(1)} APG (3G)`;

  // Win Rate calculation
  const winsIn3 = last3.filter((g) => g.isWin).length;
  const recentWinPct = (winsIn3 / count) * 100;
  const winDiff = recentWinPct - overallWinRate;
  const winIsUp = winDiff >= 0;
  const winDeltaText = `${winIsUp ? '+' : ''}${Math.round(winDiff)}% (3G)`;

  return {
    pts: { isUp: ptsIsUp, deltaText: ptsDeltaText, recentAvg: Number(recentPpg.toFixed(1)), overallAvg: Number(overallPpg.toFixed(1)) },
    reb: { isUp: rebIsUp, deltaText: rebDeltaText, recentAvg: Number(recentRpg.toFixed(1)), overallAvg: Number(overallRpg.toFixed(1)) },
    ast: { isUp: astIsUp, deltaText: astDeltaText, recentAvg: Number(recentApg.toFixed(1)), overallAvg: Number(overallApg.toFixed(1)) },
    winRate: { isUp: winIsUp, deltaText: winDeltaText, recentAvg: Math.round(recentWinPct), overallAvg: Math.round(overallWinRate) },
  };
}
